import React, {useEffect} from "react";
import {
  Box,
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from "@mui/material";
import { useSelector } from 'react-redux'; 
import RegisterStyles from "../../assets/css/register.styles";
import { RegisterProps } from "./registerProps";
import { GeneralStyles } from "../../assets/css/general.styles";
import CustomLabeledTextField from "../../components/customTextField/customLabeledTextField"; 
import theme from "../../themes/index.en";
import { InvestorModel } from "../../models/models/investorModel";
import { RegisterTouched } from "./registerTouched";
import { ValidatableComponent } from "./validatableComponent";
import CustomLabeledSelect from "../../components/customSelect/customLabeledSelect";  
import { LookupEnum } from "../../constants/enums";
import { getAverageAnnualIncome, getEstimatedWealth, getMonthlyIncomme } from "../../reducers/lookupReducer";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { RootState } from "../../store/store";
import { InvestorIncomeDetailsModel } from "../../models/models/investorIncomeDetailsModel";

const IncomeDetails: ValidatableComponent = ({
  formData,
  setFormData,
  touched,
  setTouched,
  t,
  isRTL
}) => { 
  const dispatch = useAppDispatch();
  const monthlyIncomeList = useSelector((state: RootState) => state.lookup.monthlyIncomme);
  const annualIncomeList = useSelector((state: RootState) => state.lookup.averageAnnualIncome);
  const estimatedWealthList = useSelector((state: RootState) => state.lookup.estimatedWealth);

  useEffect(() => {  
    dispatch(getMonthlyIncomme({lookupId: LookupEnum.MonthlyIncomme}));
    dispatch(getAverageAnnualIncome({lookupId: LookupEnum.AverageAnnualIncome}));
    dispatch(getEstimatedWealth({lookupId: LookupEnum.EstimatedWealth}));
  },[]); 

  useEffect(() => {  
   if(!formData.incomeDetails.hasOtherIncomeSource){
    setFormData((prevData) => ({
      ...prevData,
      incomeDetails: {
        ...prevData.incomeDetails,
        otherIncomeSource: '',
      },
    }))
   }
  },[formData.incomeDetails.hasOtherIncomeSource]); 
  
  const handleCheckboxChange = (
    field: string,
    groupFields: string[],
    checked: boolean
  ) => {
    const updatedData = groupFields.reduce((acc, groupField) => {
      acc[groupField] = groupField === field ? checked : false;
      return acc;
    }, {} as Record<string, boolean>);  
    setFormData((prevData) => ({
      ...prevData,
      incomeDetails: {
        ...prevData.incomeDetails,
        ...updatedData
      },
    }))
  }; 

  const incomeSources = [
    { label: "salaryIncome", field: "hasSalaryIncome" },
    { label: "financingIncome", field: "hasFinancingIncome" },
    { label: "privateBusinessIncome", field: "hasPrivateBusinessIncome" },
    { label: "otherPleaseSpecify", field: "hasOtherIncomeSource" },
  ];   

  const renderCheckboxes = (options: { label: string; field: string }[], groupFields: string[]) => {
    return options.map(({ label, field }) => (
     field === "hasOtherIncomeSource" ? ( 
        <Grid container item xs={12} sm={3} spacing={1}> 
            <Grid item xs={12} sm={formData.incomeDetails.hasOtherIncomeSource ? 4 : 12}>
                <FormControlLabel
                        control={
                        <Checkbox 
                        checked={!!formData.incomeDetails[field as keyof InvestorIncomeDetailsModel]} 
                        style={GeneralStyles.checkBoxStyle}
                        onChange={(e) => handleCheckboxChange(field,groupFields, e.target.checked)}
                        name={field}
                        />
                    }
                label={ t(`${label}`)}
                />
            </Grid> 
            {formData.incomeDetails.hasOtherIncomeSource &&( 
              <Grid item xs={12} sm={8}>
                      <CustomLabeledTextField
                      label={`${t('otherIncomeResource')}`}
                      isRTL={isRTL}
                      name="otherIncomeSource"
                      type="text"
                      value={formData.incomeDetails.otherIncomeSource} 
                      onChange={(e) => 
                          setFormData((prevData) => ({
                            ...prevData,
                            incomeDetails: {
                              ...prevData.incomeDetails,
                              otherIncomeSource: e.target.value,
                            },
                          }))
                      }
                      required={formData.incomeDetails.hasOtherIncomeSource ? true : false}
                      disabled={formData.incomeDetails.hasOtherIncomeSource ? false : true}
                      error={touched.otherIncomeSource && formData.incomeDetails.otherIncomeSource.trim() === "" && formData.incomeDetails.hasOtherIncomeSource}
                      helperText={
                          touched.otherIncomeSource && formData.incomeDetails.otherIncomeSource.trim() === "" && formData.incomeDetails.hasOtherIncomeSource
                          ? `${t('mandatoryField')}` 
                          : ""
                      } 
                      />
              </Grid> 
            )}
        </Grid>  
     ):(
        <Grid item xs={12} sm={3} key={field}>
            <FormControlLabel
                control={
                    <Checkbox
                    checked={!!formData.incomeDetails[field as keyof InvestorIncomeDetailsModel]}
                    style={GeneralStyles.checkBoxStyle}
                    onChange={(e) => handleCheckboxChange(field,groupFields, e.target.checked)}
                    name={field}
                    />
                }
                label={ t(`${label}`)}
            />
      </Grid>
     ) 
    ));
  };

  return (
        <Box
        display="flex"
        flexDirection="column"
        gap={2}
        mb={2}
        pl={5}
        sx={{
          textAlign: isRTL ? "right" : "left",
          direction: isRTL ? "rtl" : "ltr", 
          marginRight: '50px', 
        }}
      > 
      <Box sx={RegisterStyles.boxStyle}>
        <Typography variant="h5" gutterBottom p={1} sx={{ fontWeight: "bold" }}>
          {t('incomeSource')}
        </Typography>
        {touched.incomeSource && (
          <Typography variant="h6" gutterBottom p={1} sx={{ color: theme.palette.error.main }}>
            {t('chooseOneOption')}
          </Typography>
        )} 
        <Grid container spacing={2}>
        {renderCheckboxes(incomeSources, incomeSources.map((option) => option.field))}
        </Grid>
      </Box> 
      <Box sx={RegisterStyles.boxStyle}> 
        <Grid container item xs={12} sm={12}  mt={1} md={12} spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
                <CustomLabeledSelect
                  label={`${t('monthlyIncome')}`}
                  isRTL={isRTL}
                  name="monthlyIncomeId"
                  type="text"
                  SelectProps={{
                    children: Array.isArray(monthlyIncomeList.data) ? (
                      monthlyIncomeList.data.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {isRTL ? item.valueAr : item.valueEn}
                        </MenuItem>
                      ))
                    ) : null,
                  }}
                  value={formData.incomeDetails.monthlyIncomeId || ""}
                  onChange={(e) =>
                    setFormData((prevData) => ({
                      ...prevData,
                      incomeDetails: {
                        ...prevData.incomeDetails,
                        monthlyIncomeId: e.target.value as number ,
                      },
                    }))
                  }
                  required={true}
                  multiple={false}
                  error={touched.monthlyIncomeId && formData.incomeDetails.monthlyIncomeId === null}
                  helperText={
                    touched.monthlyIncomeId && formData.incomeDetails.monthlyIncomeId === null
                      ? `${t('chooseOneOption')}`
                      : ""
                  }
                />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <CustomLabeledSelect 
                  label={`${t('averageAnnualIncome')}`}
                  isRTL={isRTL}
                  name="averageAnnualIncomeId"
                  type="text"
                  SelectProps={{
                    children: Array.isArray(annualIncomeList.data) ? (
                      annualIncomeList.data.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {isRTL ? item.valueAr : item.valueEn}
                        </MenuItem>
                      ))
                    ) : null,
                  }}
                  value={formData.incomeDetails.averageAnnualIncomeId || ""}
                  onChange={(e) =>
                    setFormData((prevData) => ({
                      ...prevData,
                      incomeDetails: {
                        ...prevData.incomeDetails,
                        averageAnnualIncomeId: e.target.value as number,
                      },
                    }))
                  }
                  required={true}
                  multiple={false}
                  error={touched.averageAnnualIncomeId && formData.incomeDetails.averageAnnualIncomeId === null}
                  helperText={
                    touched.averageAnnualIncomeId && formData.incomeDetails.averageAnnualIncomeId === null
                      ? `${t('chooseOneOption')}`
                      : ""
                  }
                />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <CustomLabeledSelect 
                  label={`${t('estimatedWealth')}`}
                  isRTL={isRTL}
                  name="estimatedWealthId"
                  type="text"
                  SelectProps={{
                    children: Array.isArray(estimatedWealthList.data) ? (
                      estimatedWealthList.data.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {isRTL ? item.valueAr : item.valueEn}
                        </MenuItem>
                      ))
                    ) : null,
                  }}
                  value={formData.incomeDetails.estimatedWealthId || ""}
                  onChange={(e) =>
                    setFormData((prevData) => ({
                      ...prevData,
                      incomeDetails: {
                        ...prevData.incomeDetails,
                        estimatedWealthId: e.target.value as number,
                      },
                    }))
                  }
                  required={true}
                  multiple={false}
                  error={touched.estimatedWealthId && formData.incomeDetails.estimatedWealthId === null}
                  helperText={
                    touched.estimatedWealthId && formData.incomeDetails.estimatedWealthId === null
                      ? `${t('chooseOneOption')}`
                      : ""
                  }
                />
            </Grid>
        </Grid>
      </Box> 
    </Box>
  );
};

IncomeDetails.validate = (formData: InvestorModel): RegisterTouched => { 
  const newTouched = {
    incomeSource:
                ( 
                  !formData.incomeDetails.hasSalaryIncome &&
                  !formData.incomeDetails.hasFinancingIncome &&
                  !formData.incomeDetails.hasPrivateBusinessIncome &&
                  !formData.incomeDetails.hasOtherIncomeSource 
                ) ||
                (formData.incomeDetails.hasOtherIncomeSource && formData.incomeDetails.otherIncomeSource.trim() === "" ),

    monthlyIncomeId: formData.incomeDetails.monthlyIncomeId === null, 
    averageAnnualIncomeId: formData.incomeDetails.averageAnnualIncomeId === null,  
    estimatedWealthId: formData.incomeDetails.estimatedWealthId === null,  
  };
  return newTouched;
}; 

export default IncomeDetails;